import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { deleteEmpresa, listEmpresas, type Empresa } from "@/api/empresas";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { DataTable } from "@/components/ui/DataTable";
import { Input } from "@/components/ui/Field";
import { PageHeader } from "@/components/ui/PageHeader";
import { PaginationBar } from "@/components/ui/PaginationBar";
import { useConfirmDelete } from "@/components/ui/useConfirmDelete";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { EmpresaRow } from "@/components/features/empresas/EmpresaRow";

const EMPRESAS_HEADERS = [
  "Razón social",
  "NIT",
  "Contacto",
  "Teléfono",
  "Estado",
  "Acciones",
] as const;
const PAGE_SIZE = 50;

export const EmpresasPage = () => {
  const [page, setPage] = useState(1);
  const { value: search, setValue: setSearch, debounced: debouncedSearch } =
    useDebouncedSearch("", 400);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["empresas", page, debouncedSearch],
    queryFn: () => listEmpresas({ page, limit: PAGE_SIZE, q: debouncedSearch }),
  });

  const empresas = data?.data ?? [];
  const pagination = data?.pagination;

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const { open, close, description, isLoading: deleting, confirm, pending } =
    useConfirmDelete<Empresa>({
      queryKey: ["empresas", page, debouncedSearch],
      mutationFn: (empresa) => deleteEmpresa(empresa.id),
      entityLabel: "Empresa",
      successMessage: "Empresa eliminada",
      formatDescription: (empresa) =>
        `Vas a eliminar "${empresa.razon_social}" (${empresa.nit}). Esta acción no se puede deshacer.`,
      onSuccess: () => {
        void refetch();
      },
    });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Gestión de empresas"
        subtitle={`${pagination?.total ?? empresas.length} empresa(s) registrada(s)`}
      />

      <Input
        placeholder="Buscar por razón social o NIT..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-md"
      />

      <DataTable<Empresa>
        headers={EMPRESAS_HEADERS}
        data={empresas}
        isLoading={isLoading || isFetching}
        emptyMessage="No se encontraron empresas."
        rowKey={(e) => e.id}
        renderRow={(e) => <EmpresaRow empresa={e} onDelete={open} />}
      />

      {pagination ? (
        <PaginationBar
          page={page}
          totalPages={pagination.total_pages}
          onPageChange={setPage}
          itemCount={empresas.length}
          totalCount={pagination.total}
          itemLabel="empresas"
        />
      ) : null}

      <ConfirmDialog
        open={pending !== null}
        title="¿Eliminar empresa?"
        description={description}
        confirmLabel="Sí, eliminar"
        cancelLabel="Cancelar"
        destructive
        loading={deleting}
        onConfirm={confirm}
        onCancel={close}
      />
    </div>
  );
};